import React, { useState, useMemo } from 'react';
import { X, Image, Link as LinkIcon, FileText, ChevronRight, Bell, Ban, Trash2 } from 'lucide-react';

const ContactInfoPanel = ({ activeChat, messages, onClose, allUsers }) => {
    const [activeTab, setActiveTab] = useState('media');

    // Filter content
    const mediaMessages = useMemo(() =>
        messages.filter(m => m.type === 'image' || m.type === 'video'),
        [messages]);

    const docMessages = useMemo(() =>
        messages.filter(m => m.type === 'file'),
        [messages]);

    const linkMessages = useMemo(() =>
        messages.filter(m => {
            // Simple regex for links in text messages
            const urlRegex = /(https?:\/\/[^\s]+)/g;
            return m.type === 'text' && m.text.match(urlRegex);
        }),
        [messages]);

    const getSharedGroups = () => {
        // Mock data or logic to find groups in common
        return 0;
    };

    return (
        <div className="w-80 h-full border-l border-slate-200 bg-white flex flex-col shrink-0 animate-in slide-in-from-right-10 duration-300">
            {/* Header */}
            <div className="h-16 px-4 border-b border-slate-50 flex items-center justify-between bg-slate-50/50">
                <span className="font-bold text-slate-700">Contact Info</span>
                <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-100 transition-colors">
                    <X size={18} />
                </button>
            </div>

            <div className="flex-1 overflow-y-auto custom-scrollbar">
                {/* Profile Section */}
                <div className="flex flex-col items-center p-6 border-b border-slate-50 bg-white">
                    <img
                        src={activeChat.photo || `https://ui-avatars.com/api/?name=${activeChat.name}`}
                        className="w-24 h-24 rounded-full mb-3 object-cover shadow-sm border-2 border-white ring-2 ring-slate-100"
                        alt={activeChat.name}
                    />
                    <h2 className="text-lg font-bold text-slate-800 text-center">{activeChat.name}</h2>
                    <p className="text-sm text-slate-500 mt-1">{activeChat.email || activeChat.mobile}</p>
                    {activeChat.status === 'online' && (
                        <span className="inline-flex items-center gap-1 mt-2 px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-600 text-[10px] font-bold uppercase tracking-wide border border-emerald-100">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                            Online
                        </span>
                    )}
                </div>

                {/* About / Bio (Mock) */}
                <div className="p-4 border-b border-slate-50">
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">About</p>
                    <p className="text-sm text-slate-700 leading-relaxed">
                        {activeChat.about || "Hey there! I am using Daksha Organics Chat."}
                    </p>
                </div>

                {/* Media, Links, Docs Tabs */}
                <div className="p-4">
                    <div className="flex bg-slate-100 p-1 rounded-xl mb-4">
                        <button
                            onClick={() => setActiveTab('media')}
                            className={`flex-1 py-1.5 text-xs font-bold rounded-lg transition-all ${activeTab === 'media' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                        >
                            Media
                        </button>
                        <button
                            onClick={() => setActiveTab('links')}
                            className={`flex-1 py-1.5 text-xs font-bold rounded-lg transition-all ${activeTab === 'links' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                        >
                            Links
                        </button>
                        <button
                            onClick={() => setActiveTab('docs')}
                            className={`flex-1 py-1.5 text-xs font-bold rounded-lg transition-all ${activeTab === 'docs' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                        >
                            Docs
                        </button>
                    </div>

                    <div className="min-h-[200px]">
                        {/* MEDIA TAB */}
                        {activeTab === 'media' && (
                            <div className="grid grid-cols-3 gap-2">
                                {mediaMessages.length > 0 ? mediaMessages.map(m => (
                                    <div key={m.id} className="aspect-square bg-slate-100 rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity border border-slate-100">
                                        {m.type === 'image' ? (
                                            <img src={m.fileUrl} className="w-full h-full object-cover" alt="Media" />
                                        ) : (
                                            <video src={m.fileUrl} className="w-full h-full object-cover" />
                                        )}
                                    </div>
                                )) : (
                                    <div className="col-span-3 py-8 text-center text-slate-400 text-xs">No media shared</div>
                                )}
                            </div>
                        )}

                        {/* LINKS TAB */}
                        {activeTab === 'links' && (
                            <div className="space-y-3">
                                {linkMessages.length > 0 ? linkMessages.map(m => (
                                    <div key={m.id} className="flex gap-3 bg-slate-50 p-2 rounded-lg group hover:bg-indigo-50 transition-colors">
                                        <div className="bg-slate-200 text-slate-500 w-10 h-10 rounded-lg flex items-center justify-center shrink-0 group-hover:bg-indigo-100 group-hover:text-indigo-500">
                                            <LinkIcon size={18} />
                                        </div>
                                        <div className="overflow-hidden">
                                            <a href={m.text.match(/(https?:\/\/[^\s]+)/)[0]} target="_blank" rel="noopener noreferrer" className="text-xs font-medium text-slate-800 truncate hover:underline block">
                                                {m.text.match(/(https?:\/\/[^\s]+)/)[0]}
                                            </a>
                                            <p className="text-[10px] text-slate-400 mt-1">
                                                {new Date(m.timestamp).toLocaleDateString()}
                                            </p>
                                        </div>
                                    </div>
                                )) : (
                                    <div className="py-8 text-center text-slate-400 text-xs">No links shared</div>
                                )}
                            </div>
                        )}

                        {/* DOCS TAB */}
                        {activeTab === 'docs' && (
                            <div className="space-y-2">
                                {docMessages.length > 0 ? docMessages.map(m => (
                                    <div key={m.id} className="flex items-center gap-3 p-2 hover:bg-slate-50 rounded-lg transition-colors cursor-pointer border border-transparent hover:border-slate-100">
                                        <div className="w-10 h-10 bg-red-50 text-red-500 rounded-lg flex items-center justify-center font-bold text-[10px] border border-red-100">
                                            {m.text?.split('.').pop().toUpperCase() || 'FILE'}
                                        </div>
                                        <div className="flex-1 overflow-hidden">
                                            <p className="text-xs font-medium text-slate-700 truncate">{m.text}</p>
                                            <p className="text-[10px] text-slate-400">{m.fileSize || 'Unknown size'} • {new Date(m.timestamp).toLocaleDateString()}</p>
                                        </div>
                                    </div>
                                )) : (
                                    <div className="py-8 text-center text-slate-400 text-xs">No documents shared</div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Actions */}
                <div className="p-4 border-t border-slate-50 space-y-2">
                    <button className="w-full flex items-center gap-3 p-3 hover:bg-red-50 text-red-600 rounded-xl transition-colors text-sm font-medium">
                        <Ban size={18} />
                        Block {activeChat.name}
                    </button>
                    <button className="w-full flex items-center gap-3 p-3 hover:bg-slate-50 text-slate-600 rounded-xl transition-colors text-sm font-medium">
                        <Trash2 size={18} />
                        Clear Chat History
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ContactInfoPanel;
